import './style.css'
import React, { forwardRef, useRef } from "react"
import { checkWhitelist, decodeHtml, getCaret, setCaret } from "./helper"
import { md2html } from './converter'
import { prettyMarkdownInputInterface, prettyMarkdownInputParameters } from "./types"


/*
Todo:
- [ ] Clipboard, maybe
    - [ ] paste
    - [ ] copy
- [ ] Deleting empty paragraphs on firefox has degraded (Hitting delete on an empty paragraph moves the cursor to the end
      of the previous par in, e.g., `test\n{HIT-DELETE-HEREs}\nende` )  :/ -> (re)FIX -> apparently this never worked
      properly -> won't fix; perhaps include a workaround later on
- [ ] A popover for formatting
- [ ] Checkboxes (?)
- [ ] Mode-Attribute: edit | readonly | checkbox-only (once I implement them)

*/


const PrettyMarkdownInput = forwardRef<prettyMarkdownInputInterface, prettyMarkdownInputParameters>(({
    value,
    whitelist=null,
    spellCheck=false,
    showMarkdown='per-paragraph',
    onChange=null,
    updateCursor=null,
    style={},
  }, outerRef:React.ForwardedRef<any>) => {

  const initialCursorPos = value.indexOf('<span class="set-cursor" />')
  const [ content, setContent ] = React.useState(value.replace('<span class="set-cursor" />', ''))

  const pos:React.RefObject<number|null> = useRef(null)
  const divElementRef:React.RefObject<HTMLDivElement|null> = useRef(null)
  const activePar:React.RefObject<number|null> = useRef(null)

  const cssStyle = {
    ...style
  }

  React.useEffect(() => {

    if (value !== content) {
      //console.log(`render ${value} instead of ${content}`)
      const i = value.indexOf('<span class="set-cursor" />')
      setContent(value.replace('<span class="set-cursor" />', ''))
      if (i>-1) {
        divElementRef.current?.focus()
        setCaret(divElementRef.current||null, i)
      }
    }

    if (initialCursorPos>-1) {
      divElementRef.current?.focus()
      setCaret(divElementRef.current||null, initialCursorPos)
    }

    return () => {
      //console.log(`Done rendering, go to: ${pos}`, (typeof elm !== 'undefined') ? elm.innerHTML : './.')
      if (pos.current !== null) setCaret(divElementRef.current, pos.current)
    }
  })

  const blur = () => {
    if (showMarkdown !== 'always') divElementRef.current?.querySelectorAll('.show-md').forEach(n => {
      n.setAttribute('class', (n.getAttribute('class') || '').replace(/( ?)show-md( ?)/g, '$1$2').replaceAll('  ', ' '))
    })
    pos.current = 0
  }

  const focus = () => {
    if (showMarkdown === 'on-focus') divElementRef.current?.querySelectorAll('p,div,h1,h2,h3').forEach(n => {
      n.setAttribute('class', ((n.getAttribute('class') || '').replace(/( ?)show-md( ?)/g, '$1$2')+' show-md').replaceAll('  ', ' '))
    })
    pos.current = 0
  }

  const stringSplice = (s:string, start:number, deleteCount:number, replacement:string) => {
    // console.log(`Replace ${deleteCount} characters at ${start} by "${replacement}"`)
    // console.log(s.substring(0, start) + '|' + replacement + '|' + s.substring(start+deleteCount))
    if ((pos.current) && (pos.current >= deleteCount + start)) {
      pos.current += replacement.length - deleteCount
    }
    return s.substring(0, start) + replacement + s.substring(start + deleteCount)
  }

  const splice = (start:number, deleteCount:number, replacement:string) => {
    const s = stringSplice(content, start, deleteCount, replacement)
    setCaret(divElementRef.current, pos.current || 0)
    setContent(s)
    if (onChange) onChange(s)
  }

  const forwardedRef = outerRef as prettyMarkdownInputInterface
  if (forwardedRef !== null) {
    if (forwardedRef.current === null) forwardedRef.current = { div: divElementRef, goto: () => {}, splice: splice }
    forwardedRef.current.div = divElementRef
    forwardedRef.current.goto = (start:number) => {
      setCaret(divElementRef.current, Math.max(0, Math.min(start, content.length)))
    }
    forwardedRef.current.splice = splice
  }

  const fixContent = (s:string) => {

    var matches:RegExpMatchArray | null
    var length:number = 0
    var inCodeBlock = false
    var enumCounter = {
        0:0,
        1:0,
        2:0,
        3:0,
    }
    s = s.split("\n").map(s => {
      if (s.match(/^```/)) {
        inCodeBlock = !inCodeBlock
      }
      if (!inCodeBlock) {

      if ((checkWhitelist('OrderedList', whitelist)) && (matches = s.match(/^([  ]{12}|[  ]{8}|[  ]{4}|[  ]{0})([0-9]+)\.[  ]+([^\n]+)/)) && (typeof matches.index !== 'undefined')) {
        const indent = matches[1].length / 4 as 0|1|2|3
        enumCounter[indent]++
        if (matches[1] != `${enumCounter[indent]}`) s = stringSplice(s, matches.index + matches[1].length, matches[2].length, `${enumCounter[indent]}`)
        for(var i=indent+1; i<4; i++) enumCounter[i as 0|1|2|3] = 0
      }
      else {
        enumCounter[0] = 0
        enumCounter[1] = 0
        enumCounter[2] = 0
      }
    }
      length += s.length
      return s
    }).join("\n")
    return s
  }

  const replaceSelectedText = (s:string) => {
    if (!divElementRef.current) return
    pos.current = getCaret(divElementRef.current)
    const start = getCaret(divElementRef.current, 0, 'start')
    const end = getCaret(divElementRef.current, 0, 'end')
    if (pos.current === null) return
    if (!start) return
    if (!end) return
    if (start === end) {
      splice(start, 0, `${s}<span class="set-cursor" />${s}`)
    }
    else {
      const r = content.substring(start, end)
      splice(start, r.length, `${s}${r}${s}`)
    }
  }

  const updateContent = () => {
    //const src:HTMLDivElement = e.target as HTMLDivElement
    const src = divElementRef.current
    if (src === null) return

    pos.current = getCaret(src)
    divElementRef.current = src
    const clone = (src.cloneNode(true) as HTMLElement)
    clone.querySelectorAll(".overlay").forEach((e:Element) => e.remove())
    var innerHtml = clone.innerHTML

    // console.log(`deriveContentFrom(${innerHtml})`)
    const content = fixContent(
      decodeHtml(innerHtml
      .replace(/<\/(p|div|h1|h2|h3)><[^>]+>/gi, "\n")
      .replace(/<[^>]+>/g, ''))
      .replace(/^\n/, '')
      .replaceAll('<', '&lt;')
    )
    // console.log(`setContent(${content})`)
    setContent(content)
    if (onChange) onChange(content)
  }

  const selectionChange = (e:React.SyntheticEvent) => {
    const src:HTMLDivElement = e.target as HTMLDivElement
    const p = getCaret(src)
    if (p !== null) {
      const frag = content.substring(0, p)
      const pars = frag.split("\n")
      activePar.current = pars.length -1
      if (updateCursor) updateCursor(p)
      // console.log(`Par ${activePar} / ${pars[activePar].length} / ${p} <- ${frag}`)
    }

    if (showMarkdown === 'always') return
    if ((showMarkdown === 'on-focus') && (src.contains(document.activeElement))) return

    const sel = window.getSelection()
    if (!sel) return null

    try {
      const range = sel.getRangeAt(0)
      var elm:Node|null = range.startContainer
      while(elm && (elm.parentElement !== src) && ['p','div','h1', 'h2', 'h3'].indexOf(elm.nodeName.toLowerCase())<0) {
        elm=elm.parentNode
      }
      if (elm !== null) {
        const xelm = (elm as Element)
        src.parentElement?.querySelectorAll('.show-md').forEach(n => { if (n!==xelm) {
            n.setAttribute('class', (n.getAttribute('class') || '').replace(/( ?)show-md( ?)/g, '$1$2').replaceAll('  ', ' '))
        }} )
        if (xelm !== src) xelm.setAttribute('class', ((xelm.getAttribute('class')||'').replace(/( ?)show-md( ?)/g, '$1$2') + ' show-md').replaceAll('  ', ' '))
      }

    }
    catch(e) {
      console.warn(e)
      src.focus()
      setCaret(src, pos.current||0)
    }

  }

  const keyUp = (e:React.KeyboardEvent) => {
    switch(e.keyCode) {
      case 13:
        if ((activePar.current!==null) && (activePar.current>0) && (pos.current !== null)) {
          var matches
          const pars = content.split("\n")
          const s = pars[activePar.current-1]
          if (s.match('^> [^\n]+')) {
            splice(pos.current||0-1, 0, "> ")
          }
          else if (matches = s.match(/^([  ]{12}|[  ]{8}|[  ]{4}|[  ]{0})-[  ]([^\n]+)/)) {
            splice(pos.current||0-1, 0, `${matches[1]}- `)
          }
          else if (matches = s.match(/^([  ]{12}|[  ]{8}|[  ]{4}|[  ]{0})([0-9]+)\.[  ]([^\n]+)/)) {
            splice(pos.current||0-1, 0, `${matches[1]}${Number(matches[2])+1}. `)
          }
          else if (matches = s.match(/^([  ]{12}|[  ]{8}|[  ]{4})([^\n])/)) {
            splice(pos.current||0-1, 0, `${matches[1]}`)
          }
          setTimeout(() => updateContent(), 10)
        }
        break

      default:
        //console.log(e.keyCode)
    }
  }

  const keyDown = (e:React.KeyboardEvent) => {
    switch(e.keyCode) {

      case 73: // I
      case 75: // K
      case 66: // B
      case 70: // F
      case 85: // U
        if (e.ctrlKey) {
          if (divElementRef.current && ((e.keyCode===73) || (e.keyCode===75)) && (checkWhitelist('Emphasis'))) replaceSelectedText('*')
          if (divElementRef.current && ((e.keyCode===66) || (e.keyCode===70)) && (checkWhitelist('Bold'))) replaceSelectedText('**')
          if (divElementRef.current && (e.keyCode===85) && (checkWhitelist('Underline'))) replaceSelectedText('_')
          e.preventDefault()
          e.stopPropagation()
        }
        break

      case 9:
        if (!e.ctrlKey) {
          if ((activePar.current!==null) && (pos.current !== null) && (divElementRef.current)) {
            pos.current = getCaret(divElementRef.current)
            const pars = content.split("\n")
            const s = pars[activePar.current]
            var matches
            var start = 0
            for (var i=0; i<activePar.current; i++) start = start+pars[i].length + 1

            if (e.shiftKey) {
              if (matches = s.match(/^([  ]{12}|[  ]{8}|[  ]{4})-[  ]([^\n]+)/)) splice(start, 4, ``)
              else if (matches = s.match(/^([  ]{12}|[  ]{8}|[  ]{4})([0-9]+)\.[  ]([^\n]+)/)) splice(start, 4, ``)
            }
            else {
              if (matches = s.match(/^([  ]{8}|[  ]{4}|[  ]{0})-[  ]([^\n]+)/)) splice(start, 0, `    `)
              else if (matches = s.match(/^([  ]{8}|[  ]{4}|[  ]{0})([0-9]+)\.[  ]([^\n]+)/)) splice(start, 0, `    `)
            }

            setTimeout(() => updateContent(), 10)//setContent(fixContent(content))
            e.preventDefault()
            e.stopPropagation()
          }
        }
        break

      default:
        // console.log(e.keyCode)
    }
  }


  if (updateCursor) updateCursor(pos.current)

  return <div
    style={cssStyle}
    className={"pretty-markdown-input" + (content ? ' has-content' : '')}
    contentEditable="true"
    suppressContentEditableWarning={true}
    onInput={updateContent}
    onSelect={selectionChange}
    onKeyUp={keyUp}
    onKeyDown={keyDown}
    onBlur={blur}
    onFocus={focus}
    spellCheck={spellCheck}
    ref={divElementRef}
    dangerouslySetInnerHTML={{
        __html: md2html(content, activePar.current, whitelist, showMarkdown) + '<p style="display:none" class="overlay"><br /></p>'
    }} />
})


export default PrettyMarkdownInput